/* eslint-disable quote-props */
// Pure configuration for the Kiku map: colors, per-breakpoint view box, label
// and callout placement. No DOM or d3 here on purpose — `graphic.js` renders it
// in the browser and `scripts/verify_map.js` re-projects it in Node to check
// nothing lands outside the frame or on top of something else.
//
// CommonJS so plain `node` can require it without a transpile step; webpack
// handles the `import` on the browser side.

const COLORS = {
  kiku: '#C52622',
  nave: '#5194C3',
  land: '#ffffff',
  water: '#e9f5fc',
  // Coastal drop shadow under the landmasses. Borders and labels have to be
  // cool grays rather than white now that land is white.
  shadow: '#aedcf5',
  border: '#c9dae6',
  disputed: '#a9c2d1',
  leader: '#8ba0ad',
  halo: '#ffffff',
  ink: '#262626',
  muted: '#8e8e8e',
};

// Offset and blur for the land shadow, in px. Unoffset, so it reads as an even
// glow around the whole coastline rather than a light source off to one side.
// The blur carries the weight the offset used to.
const SHADOW = { dx: 0, dy: 0, blur: 3.4 };

// Type sizes and line heights, in px. Set here rather than in the stylesheet
// because the collision checker has to measure exactly what gets drawn; the
// SCSS carries only weight, style and color. 12px is the floor.
// `track` is letter-spacing in em, set as a presentation attribute alongside
// the size so the collision checker can account for the width it adds.
const FONTS = {
  country: { size: 14, lh: 16, track: 0.14 },
  water: { size: 12, lh: 14, track: 0.02 },
  place: { size: 13, lh: 15, track: 0.05 },
  vessel: { size: 12, lh: 14 },
  date: { size: 12, lh: 15 },
  text: { size: 13, lh: 15 },
};

// Zero-length dashes with round caps render as dots. Used for the blackout leg
// (inferred, not observed) and for disputed boundaries.
const DOTS = '0.1 4.5';

// The lon/lat box the map is fitted to, per breakpoint. The projection is not
// clipped to the box, so space left over by the fit shows real geography rather
// than blank paper.
//
// Framed on the Strait of Hormuz. The west edge is set by the Kiku's last AIS
// signal at 54.8E, which has to stay on the map; the Qatar loading anchorage
// 3 degrees further west does not, and the track simply enters from off-frame.
// The narrow box is fitted on width, so its latitudes only decide where the
// frame sits vertically, not the scale. They are set 0.15 south of what the
// geography needs to pull the view down into the shortened mobile frame below,
// cropping the empty water north of the strait rather than the coast below it.
const BBOX = { wide: [54.5, 24.75, 57.9, 26.75], narrow: [54.4, 24.45, 58.0, 26.8] };

// Map height as a fraction of width, per breakpoint. Squarer than a typical
// locator map because the sequence runs diagonally — the narrows sit north and
// the transfer anchorage 1.4 degrees south of them, so a short frame would jam
// one against the top edge and the other against the bottom. Mobile is cropped
// off the top, where there is only open water above the strait; the fit is on
// width, so this trims the frame without changing the scale.
const ASPECT = { wide: 0.74, narrow: 1.058 };

// What to hand `projection.fitExtent`. A Polygon would be read with d3-geo's
// spherical winding-order convention, where a ring wound the wrong way means
// "everything except this box" and the fit silently falls back to the whole
// globe. Mercator maps lon and lat independently and monotonically, so the four
// corners as a MultiPoint bound exactly the same area with no winding to get
// wrong.
function fitTarget(bbox) {
  const [w, s, e, n] = bbox;
  return { type: 'MultiPoint', coordinates: [[w, s], [e, s], [e, n], [w, n]] };
}

// Base geography labels. `c` is [lon, lat]; `dx`/`dy` nudge the text off the
// point in px; `anchor` is the SVG text-anchor. A `narrow` key overrides fields
// at the mobile breakpoint, where the frame is tighter — `narrow: null` drops
// the label there entirely.
const LABELS = {
  // Every position below is checked against the land polygons — at this zoom
  // the Iranian coast runs diagonally away from the frame, so a country label
  // a tenth of a degree too far south sits in open water.
  countries: [
    // Inland of the Makran coast east of the strait, the one part of Iran in
    // frame with room for a label — west of the narrows it is a thin coastal
    // strip. Held clear of the inlet to the west that the dark route runs up.
    // Pushed out towards the frame edge on mobile, where the middle of that
    // coast is taken by the inferred route coming down it.
    { n: 'IRAN', c: [57.45, 26.4], narrow: { c: [57.75, 26.5] } },
    // Down in the Al Dhafra desert, near the bottom of the frame and clear of
    // the coast the story runs along. The mobile frame reaches a good deal
    // further south than the wide one, hence the separate latitude.
    { n: 'U.A.E.', c: [55.5, 24.78], narrow: { c: [55.1, 24.1] } },
    // On the Musandam peninsula, the Omani exclave the strait wraps around.
    // Mobile has no room there — the peninsula is only a few labels wide and
    // the inferred route runs either side of it — so the label moves down to
    // the Al Batinah mainland, below the transfer callout and inland of the
    // departure callout, which reaches further west across a 320 board.
    { n: 'OMAN', c: [56.3, 26.1], narrow: { c: [56.25, 23.94] } },
  ],
  water: [
    // Wraps on mobile, where set across one line it reaches the left edge.
    { n: 'Persian Gulf', c: [54.85, 26.15], narrow: { n: ['Persian', 'Gulf'], c: [54.8, 26.05] } },
    { n: 'Gulf of Oman', c: [57.3, 25.4], narrow: { n: ['Gulf of', 'Oman'], c: [57.45, 25.42] } },
    // Set in the strait's northern approach, close enough to the narrows to
    // read as their label without a leader.
    { n: ['Strait of', 'Hormuz'], c: [56.24, 26.6], narrow: { c: [56.45, 26.9] } },
  ],
  places: [
    // Dropped on mobile, where the callouts need the room more than the
    // context labels do.
    {
      n: 'Dubai', c: [55.27, 25.2], dy: 15, anchor: 'middle', narrow: null,
    },
    {
      n: 'Fujairah', c: [56.34, 25.13], dx: -7, anchor: 'end', narrow: null,
    },
  ],
};

// Ship names set against their own track, in the track's color, so the two
// lines are identified where they are rather than in a key. `color` indexes
// COLORS. Dropped on mobile, where neither track has clear space beside it —
// the names are keyed above the map at that width instead.
const TRACK_LABELS = [
  {
    n: 'Kiku', c: [54.635, 25.716], color: 'kiku', narrow: null,
  },
  {
    n: 'Nave Electron', c: [57.212, 25.175], color: 'nave', narrow: null,
  },
];

// Dated callouts, in narrative order. Dates are Gulf Standard Time (UTC+4), the
// local time of the waters in frame — see scripts/build_tracks.py, which
// converts the UTC timestamps MarineTraffic exports. `at` is the point the leader
// runs to; `dx`/`dy` place the text relative to it in px; `text` is a string or
// an array of lines. `dot` names the vessel a marker belongs to, which sets its
// color: the Kiku's own positions in red, the Nave Electron's in blue. `color`
// keys COLORS for the date line, defaulting to the Kiku's red.
const CALLOUTS = [
  {
    // The track enters the frame from the west, inbound from Qatar, so this is
    // the first event on the map.
    key: 'goDark',
    at: [54.803, 25.643],
    date: 'July 31',
    text: 'Last signal',
    dx: 0,
    dy: 22,
    anchor: 'middle',
    dot: 'kiku',
    leader: false,
    // Nudged off center by a few px: the dot is close enough to the west edge
    // that a block centered on it clips at 320.
    narrow: { dx: 4, dy: 20 },
  },
  {
    // Above and left of the inferred route at both sizes: below it the label
    // runs into the reappearance callout on mobile, and into the open water the
    // route sweeps through on desktop.
    key: 'dark',
    at: [55.55, 26.05],
    date: '21 hours',
    text: 'with no signal',
    dx: -10,
    dy: -36,
    anchor: 'end',
    leader: true,
    // Ink rather than the Kiku's red: this is the one callout marking an absence
    // of positions rather than a position, so it is not tied to a dot.
    color: 'ink',
    // Held further right on mobile. The block is the same width whatever the
    // board, so at 320 the desktop offset hangs it off the left edge; this
    // clears the edge while staying above the route, which passes just below.
    narrow: { dx: 20 },
  },
  {
    // Northwest of the anchorage: due east is the tangle of positions the Kiku
    // logged while it sat there, and due north is the inferred route coming
    // down the far side of Musandam.
    key: 'reappear',
    at: [56.645, 25.157],
    date: 'Aug. 1',
    text: 'Reappears here',
    dx: -18,
    dy: -46,
    anchor: 'end',
    dot: 'kiku',
    leader: true,
    narrow: { text: ['Reappears', 'here'], dx: -12, dy: -58 },
  },
  {
    // `at` is the Nave Electron's own position when the two came alongside, so
    // its blue marker sits a few hundred metres off the Kiku's red one — the
    // pair of dots is what makes the rendezvous legible.
    key: 'transfer',
    at: [56.583, 25.161],
    date: 'Aug. 7 \u2013 8',
    text: 'Alongside the Nave Electron',
    dx: -20,
    dy: 32,
    anchor: 'end',
    dot: 'nave',
    leader: true,
    narrow: { text: ['Alongside the', 'Nave Electron'], dx: -6, dy: 36 },
  },
  {
    // Anchored to wherever the clip puts the track's exit, so it follows the
    // frame rather than a fixed coordinate. Wrapped and set well above that
    // point because the track leaves on a shallow descent towards the corner —
    // a single wide line would lie along it, and there is no room beneath.
    // The ship is named by its track label on wide, so this only carries the
    // event. Mobile has no room for a separate track label, so there the name
    // stays here.
    key: 'naveOut',
    at: null,
    date: 'Aug. 8',
    text: 'Leaves for Asia',
    dx: -10,
    dy: -34,
    anchor: 'end',
    leader: false,
    color: 'nave',
    // Below the track on mobile, where the arrowhead exits the frame — above it
    // the block sits between the line and the Gulf of Oman label.
    narrow: { text: ['Nave Electron', 'leaves for Asia'], dx: -6, dy: 22 },
  },
];

// The track segments, in draw order. `clip` trims a segment to the frame; the
// two voyages that continue beyond it are cut at the edge, the Kiku arriving
// from Qatar and the Nave Electron carrying the cargo on east. `marks` sets the
// direction arrows along a segment — the dotted stretch goes without them, so
// nothing about the inferred route reads as observed.
const SEGMENTS = [
  {
    key: 'litBefore', track: ['kiku', 'litBefore'], color: COLORS.kiku, dash: null, clip: true, marks: true,
  },
  {
    key: 'dark', track: ['kiku', 'dark'], color: COLORS.kiku, dash: DOTS, marks: false,
  },
  {
    key: 'litAfter', track: ['kiku', 'litAfter'], color: COLORS.kiku, dash: null, marks: true, arrow: true,
  },
  {
    key: 'away', track: ['nave', 'away'], color: COLORS.nave, dash: null, clip: true, marks: true, arrow: true,
  },
];

// --- shared geometry --------------------------------------------------------
// Used by both the browser renderer and the Node verifier, so the checked
// geometry is the drawn geometry.

// Trim a projected track to the frame, keeping the longest run of vertices
// inside it and meeting the boundary flush at each cut end. Both voyages run
// well past this map — the Kiku arrives from Qatar off the west side, the Nave
// Electron carries on thousands of miles east — so they are cut at the edge
// rather than bent to fit. Returns the trimmed points plus whether each end was
// cut, which decides where an arrowhead goes.
function clipToFrame(pts, W, H) {
  const inside = p => p && p[0] >= 0 && p[0] <= W && p[1] >= 0 && p[1] <= H;

  // Bisect towards `b` (outside) to find where the segment meets the boundary.
  const edge = (a, b) => {
    let lo = 0;
    let hi = 1;
    for (let k = 0; k < 24; k += 1) {
      const mid = (lo + hi) / 2;
      if (inside([a[0] + (b[0] - a[0]) * mid, a[1] + (b[1] - a[1]) * mid])) lo = mid;
      else hi = mid;
    }
    return [a[0] + (b[0] - a[0]) * lo, a[1] + (b[1] - a[1]) * lo];
  };

  let best = null;
  let run = null;
  for (let i = 0; i <= pts.length; i += 1) {
    if (i < pts.length && inside(pts[i])) {
      if (!run) run = { start: i, end: i };
      else run.end = i;
    } else if (run) {
      if (!best || run.end - run.start > best.end - best.start) best = run;
      run = null;
    }
  }
  if (!best) return { pts: [], cutHead: false, cutTail: false };

  const kept = pts.slice(best.start, best.end + 1);
  const cutHead = best.start > 0;
  const cutTail = best.end < pts.length - 1;
  if (cutHead) kept.unshift(edge(pts[best.start], pts[best.start - 1]));
  if (cutTail) kept.push(edge(pts[best.end], pts[best.end + 1]));
  return { pts: kept, cutHead, cutTail };
}

// Even spacing of direction arrows along a projected track, measured in px of
// path length rather than per vertex — position reports come in at wildly
// uneven intervals.
function chevrons(pts, spacing) {
  const out = [];
  let acc = spacing * 0.6;
  for (let i = 1; i < pts.length; i += 1) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const seg = Math.hypot(x1 - x0, y1 - y0);
    if (seg < 0.5) continue; // eslint-disable-line no-continue
    const angle = (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI;
    let t = spacing - acc;
    while (t <= seg) {
      const f = t / seg;
      out.push({ x: x0 + (x1 - x0) * f, y: y0 + (y1 - y0) * f, angle });
      t += spacing;
    }
    acc = (acc + seg) % spacing;
  }
  return out;
}

// Heading at the end of a projected track, in degrees. Measured back over at
// least `minLen` px: the last hop of an hourly AIS feed can be a pixel or two
// of drift at anchor, which on its own would point the arrowhead anywhere.
function endAngle(pts, minLen) {
  const b = pts[pts.length - 1];
  let a = pts[0];
  for (let i = pts.length - 2; i > 0; i -= 1) {
    a = pts[i];
    if (Math.hypot(b[0] - a[0], b[1] - a[1]) >= minLen) break;
  }
  return (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
}

// Resolve a label or callout for a breakpoint, applying its `narrow` override.
function resolve(d, bp) {
  if (!(bp in d)) return d;
  if (d[bp] === null) return null;
  return Object.assign({}, d, d[bp]);
}

// Resolve a list, dropping entries switched off at this breakpoint.
function listFor(list, bp) {
  return list.map(d => resolve(d, bp)).filter(Boolean);
}

module.exports = {
  ASPECT,
  BBOX,
  CALLOUTS,
  chevrons,
  clipToFrame,
  endAngle,
  COLORS,
  DOTS,
  fitTarget,
  FONTS,
  LABELS,
  listFor,
  resolve,
  SEGMENTS,
  SHADOW,
  TRACK_LABELS,
};
